import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { ComprehensiveStockAnalysis } from '@/types/perplexity.types';
import { SkeletonCard } from './SkeletonCard';

interface MarketSentimentCardProps {
  data: ComprehensiveStockAnalysis['marketSentiment'] | null | undefined;
  isLoading: boolean;
}

export const MarketSentimentCard: React.FC<MarketSentimentCardProps> = ({ data, isLoading }) => {
  if (isLoading || !data) {
    return <SkeletonCard lines={5} />;
  }

  const sentimentItems = [
    {
      icon: 'people',
      label: 'Analyst Ratings',
      value: data.analystRatings,
      color: Colors.ai.primary,
    },
    {
      icon: 'business',
      label: 'Institutional Activity',
      value: data.institutionalActivity,
      color: '#007AFF',
    },
    {
      icon: 'flash',
      label: 'Short Interest',
      value: data.shortInterest,
      color: '#FF9500',
    },
    {
      icon: 'chatbubbles',
      label: 'Retail Sentiment',
      value: data.retailSentiment,
      color: '#5856D6',
    },
  ];

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Ionicons name="pulse" size={20} color={Colors.ai.primary} />
        <Text style={styles.cardTitle}>Market Sentiment</Text>
      </View>

      {sentimentItems.map((item, index) => (
        <View key={index} style={styles.sentimentItem}>
          <View style={styles.sentimentHeader}>
            <Ionicons name={item.icon as any} size={16} color={item.color} />
            <Text style={[styles.sentimentLabel, { color: item.color }]}>{item.label}</Text>
          </View>
          <Text style={styles.sentimentValue}>{item.value}</Text>
          {index < sentimentItems.length - 1 && <View style={styles.divider} />}
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    flex: 1,
  },
  sentimentItem: {
    marginBottom: 8,
  },
  sentimentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  sentimentLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  sentimentValue: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.text.primary,
    marginLeft: 22,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginVertical: 8,
  },
});
